import { Graphics } from "pixi.js";

import { Colors, Spacing, Typography } from "../../config/DesignConstants";
import { LayerName } from "../../config/layers";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { V2d } from "../../core/Vector";
import { TextAnchor, VectorText, VerticalAnchor } from "../text/VectorText";

export type ButtonStyle = "primary" | "secondary" | "neutral";
export type ButtonSize = "small" | "medium" | "large";

export interface VectorButtonOptions {
  text: string;
  position?: V2d;
  onClick?: () => void;
  style?: ButtonStyle;
  size?: ButtonSize;
  disabled?: boolean;
  layer?: LayerName;
  color?: number; // Override color for all button states
}

interface ButtonSizeConfig {
  padding: { x: number; y: number };
  fontSize: number;
  minWidth: number;
}

interface ButtonStyleConfig {
  borderColor: number;
  textColor: number;
  hoverFillColor: number;
  hoverBorderColor: number;
  pressedFillColor: number;
}

export class VectorButton extends BaseEntity implements Entity {
  pausable = false; // Buttons should work even when game is paused

  private background: Graphics;
  private text: VectorText;
  private onClickCallback?: () => void;
  private style: ButtonStyle;
  private size: ButtonSize;
  private disabled: boolean;
  private isHovered: boolean = false;
  private isPressed: boolean = false;
  private customColor?: number;

  // Button dimensions
  private width: number = 0;
  private height: number = 0;
  private bounds: { x: number; y: number; width: number; height: number } = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };

  constructor(options: VectorButtonOptions) {
    super();

    this.onClickCallback = options.onClick;
    this.style = options.style ?? "primary";
    this.size = options.size ?? "medium";
    this.disabled = options.disabled ?? false;
    this.customColor = options.color;

    // Create background graphics
    this.background = new Graphics();
    if (options.layer) {
      (this.background as any).layerName = options.layer;
    }

    // Create text
    const sizeConfig = this.getSizeConfig();
    this.text = new VectorText({
      text: options.text,
      scale: sizeConfig.fontSize / 10, // Slightly larger than /14, smaller than /8
      strokeWidth: 2, // Thicker lines for better legibility
      color: this.getStyleConfig().textColor,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: options.layer,
    });

    // Position the button
    if (options.position) {
      this.updatePosition(options.position);
    }

    // Calculate button dimensions
    this.calculateDimensions();
  }

  private getSizeConfig(): ButtonSizeConfig {
    switch (this.size) {
      case "small":
        return {
          padding: { x: Spacing.MD, y: Spacing.SM },
          fontSize: Typography.FONT_SIZE.UI_TEXT, // Back to reasonable size
          minWidth: 80,
        };
      case "medium":
        return {
          padding: { x: Spacing.LG, y: Spacing.MD },
          fontSize: Typography.FONT_SIZE.BUTTON_TEXT, // Back to reasonable size
          minWidth: 120,
        };
      case "large":
        return {
          padding: { x: Spacing.XL, y: Spacing.LG },
          fontSize: Typography.FONT_SIZE.BUTTON_TEXT, // Back to reasonable size
          minWidth: 160,
        };
    }
  }

  private getStyleConfig(): ButtonStyleConfig {
    // Use custom color if provided
    if (this.customColor !== undefined) {
      return {
        borderColor: this.customColor,
        textColor: this.customColor,
        hoverFillColor: this.customColor,
        hoverBorderColor: this.customColor,
        pressedFillColor: this.customColor,
      };
    }

    switch (this.style) {
      case "primary":
        return {
          borderColor: Colors.CYAN,
          textColor: Colors.CYAN,
          hoverFillColor: Colors.CYAN,
          hoverBorderColor: Colors.CYAN,
          pressedFillColor: Colors.CYAN,
        };
      case "secondary":
        return {
          borderColor: Colors.CYAN,
          textColor: Colors.CYAN,
          hoverFillColor: Colors.CYAN,
          hoverBorderColor: Colors.CYAN,
          pressedFillColor: Colors.CYAN,
        };
      case "neutral":
        return {
          borderColor: Colors.WHITE,
          textColor: Colors.WHITE,
          hoverFillColor: Colors.WHITE,
          hoverBorderColor: Colors.WHITE,
          pressedFillColor: Colors.WHITE,
        };
    }
  }

  private calculateDimensions(): void {
    const sizeConfig = this.getSizeConfig();
    const textWidth = this.text.calculateTextWidth();

    this.width = Math.max(
      sizeConfig.minWidth,
      textWidth + sizeConfig.padding.x * 2,
    );
    this.height = this.text.calculateTextHeight() + sizeConfig.padding.y * 2;

    // Update bounds for hit testing
    this.updateBounds();
  }

  private updateBounds(): void {
    // Only update bounds if we have a sprite (after onAdd is called)
    if (this.sprite) {
      const position = this.getPosition();
      this.bounds = {
        x: position.x - this.width / 2,
        y: position.y - this.height / 2,
        width: this.width,
        height: this.height,
      };
    }
  }

  private updatePosition(position: V2d): void {
    this.background.position.set(position.x, position.y);
    this.text.position = position;
    this.updateBounds();
  }

  private isPointInside(x: number, y: number): boolean {
    return (
      x >= this.bounds.x &&
      x <= this.bounds.x + this.bounds.width &&
      y >= this.bounds.y &&
      y <= this.bounds.y + this.bounds.height
    );
  }

  private redrawButton(): void {
    this.background.clear();

    if (this.disabled) {
      this.drawDisabledButton();
    } else if (this.isPressed) {
      this.drawPressedButton();
    } else if (this.isHovered) {
      this.drawHoveredButton();
    } else {
      this.drawNormalButton();
    }
  }

  private drawNormalButton(): void {
    const styleConfig = this.getStyleConfig();

    // Draw border
    this.background.rect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
    );
    this.background.stroke({
      color: styleConfig.borderColor,
      width: 2,
    });

    // Reset text stroke to normal (thicker than default)
    this.text.strokeWidth = 2;
  }

  private drawHoveredButton(): void {
    const styleConfig = this.getStyleConfig();

    // Draw thicker border for hover effect
    this.background.rect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
    );
    this.background.stroke({
      color: styleConfig.hoverBorderColor,
      width: 4, // Thicker stroke for hover
    });

    // Add outer glow effect with even thicker stroke
    this.background.rect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
    );
    this.background.stroke({
      color: styleConfig.hoverBorderColor,
      width: 6,
      alpha: 0.3,
    });

    // Make the text even thicker on hover
    this.text.strokeWidth = 3;
  }

  private drawPressedButton(): void {
    const styleConfig = this.getStyleConfig();

    // Draw thicker border for pressed effect
    this.background.rect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
    );
    this.background.stroke({
      color: styleConfig.borderColor,
      width: 3, // Slightly thicker for pressed state
    });

    // Make text thicker when pressed
    this.text.strokeWidth = 3;
  }

  private drawDisabledButton(): void {
    const styleConfig = this.getStyleConfig();

    // Draw border with reduced opacity
    this.background.rect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
    );
    this.background.stroke({
      color: styleConfig.borderColor,
      width: 2,
      alpha: 0.3,
    });

    // Reset text stroke to normal for disabled state
    this.text.strokeWidth = 2;
  }

  onAdd(): void {
    this.sprite = this.background;
    this.addChild(this.text);
    this.redrawButton();
  }

  onRender(): void {
    // Check if mouse position has changed and update hover state
    if (this.game && !this.disabled) {
      const mousePos = this.game.io.mousePosition;
      const wasHovered = this.isHovered;
      this.isHovered = this.isPointInside(mousePos.x, mousePos.y);

      if (wasHovered !== this.isHovered) {
        this.redrawButton();
      }
    }
  }

  onMouseDown(): void {
    if (this.disabled) return;

    if (this.game) {
      const mousePos = this.game.io.mousePosition;
      if (this.isPointInside(mousePos.x, mousePos.y)) {
        this.isPressed = true;
        this.redrawButton();
      }
    }
  }

  onMouseUp(): void {
    if (this.disabled) return;

    if (this.isPressed) {
      this.isPressed = false;
      this.redrawButton();
    }
  }

  onClick(): void {
    if (this.disabled) return;

    if (this.game) {
      const mousePos = this.game.io.mousePosition;
      if (this.isPointInside(mousePos.x, mousePos.y)) {
        this.onClickCallback?.();
      }
    }
  }

  // Public methods for updating button properties
  setText(text: string): void {
    this.text.text = text;
    this.calculateDimensions();
    this.redrawButton();
  }

  setDisabled(disabled: boolean): void {
    this.disabled = disabled;
    this.isHovered = false;
    this.isPressed = false;

    // Update text color
    const styleConfig = this.getStyleConfig();
    this.text.color = disabled ? Colors.CYAN : styleConfig.textColor;

    this.redrawButton();
  }

  setStyle(style: ButtonStyle): void {
    this.style = style;
    const styleConfig = this.getStyleConfig();
    this.text.color = this.disabled ? Colors.CYAN : styleConfig.textColor;
    this.redrawButton();
  }

  setPosition(position: V2d): void {
    this.updatePosition(position);
  }

  getSize(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }
}
